import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi, useLoaderData } from '@tanstack/react-router';

import { getMediaOptions, useProjectMedia } from '@/api/media';
import type { UploadMediaModalType } from '@/features/estate-media/components/upload-media-modal';
import {
  ACCEPTED_FLOORPLANS_INPUT_TYPE,
  uploadLayoutsSchema,
} from '@/features/estate-media/schema/upload-media';
import { canWrite } from '@/features/permissions/resource-permissions';
import { useDeleteFloorLayouts } from '@/features/project-media/api/delete-floor-layouts';
import { useUploadFloorLayouts } from '@/features/project-media/api/upload-floor-layout';

import { MediaData } from './media-data';
import { UploadMediaModal } from '../../estate-media/components/upload-media-modal';
import { useModal } from '../../../components/modals/new/use-modal';
import { ConfirmationModal } from '../../../components/modals/confirmation-modal';

const route = getRouteApi(
  '/_authenticated/_sidebar-layout/projects/$projectId/_dashboard-layout/media/floor-plans'
);

export const FloorPlansDashboard = () => {
  const uploadModal = useModal();
  const deleteModal = useModal();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deleteCallback, setDeleteCallback] = useState<() => void>(() => () => {});
  const { projectId } = route.useParams();
  const project = useLoaderData({
    from: '/_authenticated/_sidebar-layout/projects/$projectId/_dashboard-layout',
  });
  const canWriteMedia = canWrite(project.metadata?.permissions, 'media');

  const { mutateAsync: uploadFloorLayouts } = useUploadFloorLayouts({
    mutationConfig: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getMediaOptions(projectId).queryKey });
        toast.success(t('successfullyDeletedImage'));
      },
    },
  });

  const { mutateAsync: deleteFloorLayouts } = useDeleteFloorLayouts({
    mutationConfig: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getMediaOptions(projectId).queryKey });
      },
    },
  });

  const submitHandler = async (data: UploadMediaModalType) => {
    // Convert File[] to FileList
    const dataTransfer = new DataTransfer();
    data.files.forEach((file) => dataTransfer.items.add(file));
    await uploadFloorLayouts({ layouts: dataTransfer.files, projectId });
    uploadModal.dismiss();
  };

  const handleOpenModal = () => {
    uploadModal.open();
  };

  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const handleDelete = (selectedIds: string[], callback = () => {}) => {
    setSelectedIds(selectedIds);
    setDeleteCallback(() => callback);
    deleteModal.open();
  };

  const { data } = useProjectMedia({ id: projectId });

  return (
    <>
      <MediaData
        data={data.layouts || []}
        onUpload={handleOpenModal}
        handleDelete={handleDelete}
        canWriteMedia={canWriteMedia}
        page="floor-plans"
        type="project"
        projectId={projectId}
        isThumbnailDisabled
      />
      <UploadMediaModal
        modal={uploadModal}
        submitHandler={submitHandler}
        acceptedType={ACCEPTED_FLOORPLANS_INPUT_TYPE}
        acceptedMediaSchema={uploadLayoutsSchema()}
      />
      <ConfirmationModal
        modal={deleteModal}
        title={t('deleteSelectedTitle', { count: selectedIds.length })}
        description={t('deleteSelectedDescription')}
        confirmationText={t('delete')}
        destructive
        onSubmit={async () => {
          await deleteFloorLayouts({ projectId, layoutIds: selectedIds });
          deleteCallback();
        }}
      />
    </>
  );
};
